import { el, clear } from "./render";
import copy from "./copy/landing.en.json";
import "./landing.css";

/**
 * What someone who has never heard of nicemail sees.
 *
 * The page follows docs/brand/website-brand-bible.md. Every visible string comes
 * from copy/landing.en.json, so the copy can be edited and translated there.
 *
 * There is no self-serve signup yet, and the page does not pretend otherwise.
 * It takes an address so there is somebody to invite when there is something to
 * invite them to, and says so in those words.
 */

export interface LandingOptions {
  /** Show the sign-in screen: for the people who already have an account. */
  onSignIn: () => void;
  /** Overridden in tests. */
  submit?: (address: string) => Promise<Response>;
}

type Copy = typeof copy;

/** Keyed by the box id in the copy file, so renaming or translating a box keeps its icon. */
const BOX_ICONS: Record<string, string> = {
  stack: "M12 4l8 4-8 4-8-4zM4 12l8 4 8-4M4 16l8 4 8-4",
  news: "M5 5h11v14H7a2 2 0 0 1-2-2zM16 9h3v8a2 2 0 0 1-2 2M8 9h5M8 13h5M8 16h3",
  "paper-trail": "M7 3h7l4 4v14H7zM14 3v4h4M10 12h5M10 16h5",
  maybes: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5V14M12 17v.01",
};
const KEY_ICON = "M15 3a6 6 0 1 1 0 12 6 6 0 0 1 0-12zM10.8 13.2L3 21M6 18l2.5 2.5M8.5 15.5L11 18M15 7.5v.01";

function icon(path: string): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const p = document.createElementNS(ns, "path");
  p.setAttribute("d", path);
  svg.append(p);
  return svg;
}

function photo(src: string, alt: string, cls = ""): HTMLImageElement {
  return el("img", { class: cls, src, alt, loading: "lazy", decoding: "async" });
}

function shot(src: string, alt: string): HTMLElement {
  return el("figure", { class: "lp-shot" },
    el("div", { class: "lp-shot-bar", "aria-hidden": "true" }, el("i", {}), el("i", {}), el("i", {})),
    photo(src, alt));
}

function eyebrow(text: string): HTMLElement {
  return el("p", { class: "lp-eyebrow" }, text);
}

function inviteForm(c: Copy["invite"], submit: (address: string) => Promise<Response>): HTMLFormElement {
  const input = el("input", {
    id: "early-access",
    type: "email",
    autocomplete: "email",
    spellcheck: "false",
    placeholder: c.placeholder,
    required: "true",
  });
  const button = el("button", { class: "primary", type: "submit" }, c.button);
  const said = el("p", { class: "note", role: "status" });
  said.hidden = true;

  const form = el("form", { class: "lp-invite" },
    el("label", { for: "early-access" }, c.label),
    el("div", { class: "lp-invite-row" }, input, button),
    said,
    el("p", { class: "lp-fineprint" }, c.fineprint));

  const failed = (text: string): void => {
    said.className = "error";
    said.textContent = text;
    button.disabled = false;
  };

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const address = input.value.trim();
    if (!address) return;
    button.disabled = true;
    said.hidden = false;
    said.className = "note";
    said.textContent = c.sending;

    void submit(address)
      .then(async (response) => {
        if (response.ok) {
          // The form is replaced rather than reset: a form still sitting there reads as a failure.
          form.replaceChildren(
            el("p", { class: "note lp-done", role: "status" }, c.done.replace("{address}", address)));
          return;
        }
        failed((await response.text().catch(() => "")) || c.error);
      })
      .catch(() => failed(c.error));
  });
  return form;
}

export function renderLanding(
  root: HTMLElement,
  picker: HTMLSelectElement,
  options: LandingOptions,
): void {
  clear(root);
  document.title = copy.meta.title;
  const submit = options.submit ?? ((address: string) =>
    fetch("/waitlist", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ address }),
    }));

  const signInButton = (text: string, cls: string): HTMLButtonElement => {
    const button = el("button", { class: cls, type: "button" }, text);
    button.addEventListener("click", options.onSignIn);
    return button;
  };

  const form = inviteForm(copy.invite, submit);
  const toInvite = (): void => {
    const input = root.querySelector<HTMLInputElement>("#early-access");
    if (input) {
      input.scrollIntoView({ behavior: "smooth", block: "center" });
      input.focus({ preventScroll: true });
    } else {
      root.querySelector(".lp-hero")?.scrollIntoView({ behavior: "smooth" });
    }
  };
  const inviteButton = (text: string, cls: string): HTMLButtonElement => {
    const button = el("button", { class: cls, type: "button" }, text);
    button.addEventListener("click", toInvite);
    return button;
  };

  const header = el("header", { class: "lp-header" },
    el("a", { class: "lp-logo", href: "#top", "aria-label": "nicemail.ca" },
      el("img", { src: "/brand/nicemail-horizontal-color.svg", alt: "nicemail" })),
    el("nav", { "aria-label": copy.nav.label },
      el("a", { href: "#boxes" }, copy.nav.boxes),
      el("a", { href: "#screener" }, copy.nav.screener),
      el("a", { href: "#difference" }, copy.nav.difference),
      el("a", { href: "#security" }, copy.nav.security)),
    el("div", { class: "lp-header-actions" },
      signInButton(copy.nav.signIn, "lp-link"),
      inviteButton(copy.nav.invite, "lp-pill")));

  const hero = el("section", { class: "lp-hero", id: "top" },
    photo("/photos/hero-maple-leaf.webp", copy.hero.imageAlt, "lp-hero-photo"),
    el("div", { class: "lp-hero-inner" },
      el("div", { class: "lp-hero-card" },
        eyebrow(copy.hero.eyebrow),
        el("h1", {}, copy.hero.title),
        el("p", { class: "lp-lede" }, copy.hero.lede),
        form,
        el("p", { class: "lp-fineprint lp-have-account" },
          `${copy.invite.haveAccount} `, signInButton(copy.invite.signIn, "lp-link")))));

  const boxes = el("section", { class: "lp-section lp-boxes", id: "boxes" },
    el("div", { class: "lp-wrap lp-center" },
      eyebrow(copy.boxes.eyebrow),
      el("h2", {}, copy.boxes.title),
      el("p", { class: "lp-lede" }, copy.boxes.lede),
      el("ul", { class: "lp-box-list" },
        ...copy.boxes.items.map((item) =>
          el("li", { class: `lp-box lp-box-${item.id}` },
            el("span", { class: "lp-box-disc" }, icon(BOX_ICONS[item.id] ?? "")),
            el("h3", {}, item.name),
            el("p", {}, item.line))))));

  const screener = el("section", { class: "lp-split lp-split-sage", id: "screener" },
    el("div", { class: "lp-split-text" },
      eyebrow(copy.screener.eyebrow),
      el("h2", {}, copy.screener.title),
      el("p", {}, copy.screener.body),
      el("ul", { class: "lp-ticks" }, ...copy.screener.points.map((p) => el("li", {}, p)))),
    el("div", { class: "lp-split-media" },
      photo("/photos/screener-addressing-envelope.webp", copy.screener.imageAlt, "lp-split-photo"),
      shot("/screens/app-screener.png", copy.screener.shotAlt)));

  const feed = el("section", { class: "lp-split lp-split-peach lp-split-flip", id: "feed" },
    el("div", { class: "lp-split-text" },
      eyebrow(copy.feed.eyebrow),
      el("h2", {}, copy.feed.title),
      el("p", {}, copy.feed.body)),
    el("div", { class: "lp-split-media" },
      photo("/photos/feed-kraft-letters.webp", copy.feed.imageAlt, "lp-split-photo"),
      shot("/screens/app-feed.png", copy.feed.shotAlt)));

  const mark = (yes: boolean): HTMLElement =>
    el("span", { class: yes ? "lp-yes" : "lp-no", role: "img", "aria-label": yes ? copy.difference.yes : copy.difference.no },
      yes ? "✓" : "✕");
  const difference = el("section", { class: "lp-section", id: "difference" },
    el("div", { class: "lp-wrap lp-center" },
      eyebrow(copy.difference.eyebrow),
      el("h2", {}, copy.difference.title),
      el("p", { class: "lp-lede" }, copy.difference.lede),
      el("table", { class: "lp-table" },
        el("thead", {}, el("tr", {},
          el("th", { scope: "col" }, el("span", { class: "lp-visually-hidden" }, copy.difference.title)),
          el("th", { scope: "col" }, copy.difference.them),
          el("th", { scope: "col", class: "lp-us" }, copy.difference.us))),
        el("tbody", {}, ...copy.difference.rows.map((row) =>
          el("tr", {}, el("th", { scope: "row" }, row), el("td", {}, mark(false)), el("td", { class: "lp-us" }, mark(true))))))));

  const security = el("section", { class: "lp-section lp-security", id: "security" },
    el("div", { class: "lp-wrap lp-center" },
      el("span", { class: "lp-security-key" }, icon(KEY_ICON)),
      eyebrow(copy.security.eyebrow),
      el("h2", {}, copy.security.title),
      el("p", { class: "lp-lede" }, copy.security.body),
      el("ul", { class: "lp-cards lp-security-points" }, ...copy.security.points.map((point) =>
        el("li", { class: "lp-card" }, el("h3", {}, point.title), el("p", {}, point.body)))),
      el("p", { class: "lp-security-link" }, `${copy.security.linkLead} `,
        el("a", { href: copy.security.linkHref, target: "_blank", rel: "noopener noreferrer" }, copy.security.linkText))));

  const monday = el("section", { class: "lp-monday" },
    el("div", { class: "lp-wrap lp-monday-inner" },
      el("div", { class: "lp-monday-media" },
        photo("/photos/monday-sleepy-plant.webp", copy.monday.imageAlt, "lp-monday-photo"),
        el("img", { class: "lp-monday-mark", src: "/brand/nicemail-symbol-accent.svg", alt: "", "aria-hidden": "true" })),
      el("div", {},
        eyebrow(copy.monday.eyebrow),
        el("h2", {}, copy.monday.title),
        el("p", {}, copy.monday.body))));

  const promises = el("section", { class: "lp-section lp-promises" },
    el("div", { class: "lp-wrap lp-center" },
      eyebrow(copy.promises.eyebrow),
      el("h2", {}, copy.promises.title),
      el("ul", { class: "lp-cards" }, ...copy.promises.items.map((item) =>
        el("li", { class: "lp-card" },
          el("img", { src: "/brand/nicemail-symbol-accent.svg", alt: "", "aria-hidden": "true" }),
          el("h3", {}, item.title),
          el("p", {}, item.body))))));

  const closing = el("section", { class: "lp-closing" },
    el("div", { class: "lp-wrap lp-center" },
      el("h2", {}, copy.closing.title),
      el("p", { class: "lp-lede" }, copy.closing.lede),
      inviteButton(copy.closing.button, "primary lp-closing-button")));

  const footer = el("footer", { class: "lp-footer" },
    el("div", { class: "lp-wrap lp-footer-inner" },
      el("img", { src: "/brand/nicemail-horizontal-color.svg", alt: copy.footer.rights, class: "lp-footer-logo" }),
      el("p", { class: "lp-rights" }, copy.footer.rights),
      el("p", { class: "lp-credits" }, copy.footer.photos),
      el("div", { class: "mark lp-mark" },
        el("span", { class: "dots" }, el("i", {}), el("i", {}), el("i", {})),
        el("span", {}, copy.footer.maker),
        picker)));

  root.append(el("div", { class: "lp" },
    header,
    el("main", {}, hero, boxes, screener, feed, difference, security, monday, promises, closing),
    footer));
}
